/* ==========================================================================
   ELYSIUM NATURALS — Animation Engine
   Requires (deferred in theme.liquid): GSAP 3 + ScrollTrigger, Lenis
   All effects respect prefers-reduced-motion and degrade gracefully.
   ========================================================================== */
(() => {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const touch = window.matchMedia('(hover: none), (pointer: coarse)').matches;
  const hasGSAP = typeof window.gsap !== 'undefined';

  document.body.classList.add('ely-body');

  /* ---------------------------------------------------------------- Lenis */
  let lenis = null;
  if (!reduced && typeof window.Lenis !== 'undefined') {
    lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    document.documentElement.classList.add('ely-lenis');
    if (hasGSAP && window.ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add((t) => lenis.raf(t * 1000));
      gsap.ticker.lagSmoothing(0);
    } else {
      const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
  } else if (hasGSAP && window.ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
  }

  /* ------------------------------------------------------ Text reveals */
  function splitWords(el) {
    if (el.dataset.elySplit) return;
    el.dataset.elySplit = '1';
    const text = el.textContent.trim();
    el.setAttribute('aria-label', text);
    el.textContent = '';
    text.split(/\s+/).forEach((word, i) => {
      const w = document.createElement('span');
      w.className = 'ely-word';
      w.setAttribute('aria-hidden', 'true');
      const inner = document.createElement('span');
      inner.className = 'ely-word-inner';
      if (el.dataset.elyChars !== undefined) {
        word.split('').forEach((c) => {
          const ch = document.createElement('span');
          ch.className = 'ely-char';
          ch.textContent = c;
          inner.appendChild(ch);
        });
      } else {
        inner.textContent = word;
      }
      w.appendChild(inner);
      el.appendChild(w);
      if (i < text.split(/\s+/).length - 1) el.appendChild(document.createTextNode(' '));
    });
  }

  function initTextReveals() {
    document.querySelectorAll('.ely-reveal-text').forEach((el) => {
      if (reduced || !hasGSAP) return;
      splitWords(el);
      const targets = el.dataset.elyChars !== undefined
        ? el.querySelectorAll('.ely-char')
        : el.querySelectorAll('.ely-word-inner');
      if (el.dataset.elyChars !== undefined) {
        gsap.set(el.querySelectorAll('.ely-word-inner'), { y: 0 });
        gsap.set(targets, { y: '110%' });
      }
      gsap.to(targets, {
        y: 0,
        duration: 1.1,
        ease: 'power4.out',
        stagger: el.dataset.elyChars !== undefined ? 0.02 : 0.06,
        scrollTrigger: { trigger: el, start: 'top 85%', once: true }
      });
    });
  }

  /* ------------------------------------------------------ Section fades */
  function initFades() {
    const els = document.querySelectorAll('.ely-fade-in');
    if (reduced || !hasGSAP) { els.forEach((e) => { e.style.opacity = 1; e.style.transform = 'none'; }); return; }
    els.forEach((el) => {
      gsap.to(el, {
        opacity: 1, y: 0, duration: 1.2, ease: 'power3.out',
        delay: parseFloat(el.dataset.elyDelay || 0),
        scrollTrigger: { trigger: el, start: 'top 88%', once: true }
      });
    });
  }

  /* ------------------------------------------------------ Parallax */
  function initParallax() {
    if (reduced || !hasGSAP) return;
    document.querySelectorAll('.ely-parallax img').forEach((img) => {
      gsap.fromTo(img, { yPercent: -10 }, {
        yPercent: 10, ease: 'none',
        scrollTrigger: { trigger: img.closest('.ely-parallax'), scrub: 0.6, start: 'top bottom', end: 'bottom top' }
      });
    });
  }

  /* ------------------------------------------------------ Dividers */
  function initDividers() {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && e.target.classList.add('is-inview'));
    }, { threshold: 0.5 });
    document.querySelectorAll('.ely-divider').forEach((d) => io.observe(d));
  }

  /* ------------------------------------------------------ Magnetic buttons */
  function initMagnetic() {
    if (touch || reduced || !hasGSAP) return;
    document.querySelectorAll('.ely-btn, [data-ely-magnetic]').forEach((btn) => {
      const strength = 0.35;
      btn.addEventListener('mousemove', (e) => {
        const r = btn.getBoundingClientRect();
        gsap.to(btn, {
          x: (e.clientX - r.left - r.width / 2) * strength,
          y: (e.clientY - r.top - r.height / 2) * strength,
          duration: 0.4, ease: 'power2.out'
        });
      });
      btn.addEventListener('mouseleave', () => {
        gsap.to(btn, { x: 0, y: 0, duration: 0.7, ease: 'elastic.out(1, 0.4)' });
      });
    });
  }

  /* ------------------------------------------------------ Ripple */
  function initRipple() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.ely-btn');
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const d = Math.max(r.width, r.height);
      const s = document.createElement('span');
      s.className = 'ely-ripple';
      s.style.cssText = `width:${d}px;height:${d}px;left:${e.clientX - r.left - d / 2}px;top:${e.clientY - r.top - d / 2}px`;
      btn.appendChild(s);
      setTimeout(() => s.remove(), 700);
    });
  }

  /* ------------------------------------------------------ Custom cursor */
  function initCursor() {
    if (touch || reduced) return;
    const c = document.createElement('div');
    c.className = 'ely-cursor';
    document.body.appendChild(c);
    let x = -100, y = -100, cx = -100, cy = -100;
    window.addEventListener('mousemove', (e) => { x = e.clientX; y = e.clientY; }, { passive: true });
    (function loop() {
      cx += (x - cx) * 0.18; cy += (y - cy) * 0.18;
      c.style.transform = `translate(${cx}px, ${cy}px) translate(-50%, -50%)`;
      requestAnimationFrame(loop);
    })();
    document.addEventListener('mouseover', (e) => {
      c.classList.toggle('is-hover', !!e.target.closest('a, button, .ely-card'));
    });
  }

  /* ------------------------------------------------------ Card tilt */
  function initCardTilt() {
    if (touch || reduced || !hasGSAP) return;
    document.querySelectorAll('.ely-card').forEach((card) => {
      card.addEventListener('mousemove', (e) => {
        const r = card.getBoundingClientRect();
        gsap.to(card, {
          rotateY: ((e.clientX - r.left) / r.width - 0.5) * 6,
          rotateX: -((e.clientY - r.top) / r.height - 0.5) * 6,
          y: -10, duration: 0.5, ease: 'power2.out', transformPerspective: 800
        });
      });
      card.addEventListener('mouseleave', () => {
        gsap.to(card, { rotateX: 0, rotateY: 0, y: 0, duration: 0.9, ease: 'elastic.out(1, 0.5)' });
      });
    });
  }

  /* ------------------------------------------------------ Grid stagger */
  function initGridStagger() {
    if (reduced || !hasGSAP) return;
    document.querySelectorAll('.ely-grid').forEach((grid) => {
      const cards = grid.querySelectorAll('.ely-card');
      gsap.set(cards, { opacity: 0, y: 50, scale: 0.96, rotate: 0.6 });
      ScrollTrigger.batch(cards, {
        start: 'top 90%',
        onEnter: (batch) => gsap.to(batch, {
          opacity: 1, y: 0, scale: 1, rotate: 0,
          duration: 1, ease: 'power3.out', stagger: 0.09, overwrite: true
        })
      });
    });
  }

  /* ------------------------------------------------------ Page transitions */
  function initPageTransitions() {
    if (reduced || !hasGSAP) return;
    const veil = document.createElement('div');
    veil.className = 'ely-veil';
    document.body.appendChild(veil);
    // Reveal on load
    gsap.set(veil, { scaleY: 1, transformOrigin: 'top' });
    gsap.to(veil, { scaleY: 0, duration: 0.9, ease: 'power4.inOut', delay: 0.1 });
    // Cover on internal navigation
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a || a.target === '_blank' || e.metaKey || e.ctrlKey) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin || url.pathname === location.pathname) return;
      if (a.closest('[data-no-transition]')) return;
      e.preventDefault();
      gsap.set(veil, { transformOrigin: 'bottom' });
      gsap.to(veil, {
        scaleY: 1, duration: 0.6, ease: 'power4.inOut',
        onComplete: () => { location.href = url.href; }
      });
    });
    window.addEventListener('pageshow', (e) => { if (e.persisted) gsap.set(veil, { scaleY: 0 }); });
  }

  /* ------------------------------------------------------ Count-up */
  function countUp(el, to, prefix = '', duration = 0.9) {
    if (reduced || !hasGSAP) { el.textContent = prefix + to.toFixed(2); return; }
    const obj = { v: parseFloat(el.dataset.elyCurrent || 0) };
    gsap.to(obj, {
      v: to, duration, ease: 'power2.out',
      onUpdate: () => { el.textContent = prefix + obj.v.toFixed(2); }
    });
    el.dataset.elyCurrent = to;
  }

  /* ------------------------------------------------------ Cart drawer */
  const Cart = {
    drawer: null, overlay: null, itemsEl: null, subEl: null, shipFill: null, shipLabel: null,
    threshold: 75, // free shipping threshold in shop currency

    init() {
      this.drawer = document.getElementById('ElyCartDrawer');
      if (!this.drawer) return;
      this.overlay = document.getElementById('ElyCartOverlay');
      this.itemsEl = this.drawer.querySelector('[data-ely-cart-items]');
      this.subEl = this.drawer.querySelector('[data-ely-cart-subtotal]');
      this.shipFill = this.drawer.querySelector('.ely-ship__fill');
      this.shipLabel = this.drawer.querySelector('[data-ely-ship-label]');
      this.threshold = parseFloat(this.drawer.dataset.freeShipping || 75);

      this.overlay.addEventListener('click', () => this.close());
      this.drawer.querySelector('[data-ely-cart-close]').addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

      // Open triggers (header cart icon etc.)
      document.querySelectorAll('[data-ely-cart-open], a[href="/cart"]').forEach((el) => {
        el.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
      });

      // Intercept product forms → AJAX add
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action*="/cart/add"]');
        if (!form) return;
        e.preventDefault();
        this.add(form);
      });

      // Qty & remove (delegated)
      this.itemsEl.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-ely-qty]');
        if (!btn) return;
        const row = btn.closest('[data-line]');
        const line = parseInt(row.dataset.line, 10);
        const current = parseInt(row.querySelector('[data-ely-qty-value]').textContent, 10);
        const delta = btn.dataset.elyQty === '+' ? 1 : -1;
        this.change(line, current + delta);
      });
    },

    async add(form) {
      const btn = form.querySelector('[type="submit"]');
      const label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
      try {
        const res = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form) });
        if (!res.ok) throw new Error((await res.json()).description || 'Could not add to cart');
        await this.refresh();
        this.open();
      } catch (err) {
        alert(err.message);
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = label; }
      }
    },

    async change(line, quantity) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity: Math.max(0, quantity) })
      });
      await this.refresh();
    },

    async refresh() {
      const cart = await (await fetch('/cart.js')).json();
      this.render(cart);
      document.querySelectorAll('[data-ely-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    },

    render(cart) {
      this.itemsEl.innerHTML = '';
      if (!cart.items.length) {
        this.itemsEl.innerHTML = '<p style="text-align:center;padding:4rem 0;color:var(--ely-stone)">Your cart is empty.</p>';
      }
      cart.items.forEach((item, i) => {
        const row = document.createElement('div');
        row.className = 'ely-drawer__item';
        row.dataset.line = i + 1;
        row.style.animationDelay = (i * 0.08) + 's';
        row.innerHTML = `
          <img src="${item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/, '_168x$1$2') : ''}" alt="" loading="lazy">
          <div>
            <p style="font-family:var(--ely-font-display);font-size:1.1rem;margin:0 0 .3rem">${item.product_title}</p>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p style="font-size:.8rem;color:var(--ely-stone);margin:0 0 .6rem">${item.variant_title}</p>` : ''}
            <div class="ely-qty">
              <button data-ely-qty="-" aria-label="Decrease quantity">−</button>
              <span data-ely-qty-value>${item.quantity}</span>
              <button data-ely-qty="+" aria-label="Increase quantity">+</button>
            </div>
          </div>
          <span style="font-size:.95rem">${(item.final_line_price / 100).toLocaleString(undefined, { style: 'currency', currency: cart.currency })}</span>`;
        this.itemsEl.appendChild(row);
      });

      const subtotal = cart.total_price / 100;
      countUp(this.subEl, subtotal, this.subEl.dataset.symbol || '$');

      const pct = Math.min(100, (subtotal / this.threshold) * 100);
      this.shipFill.style.width = pct + '%';
      this.shipLabel.textContent = pct >= 100
        ? 'Complimentary shipping unlocked'
        : `You are ${(this.threshold - subtotal).toLocaleString(undefined, { style: 'currency', currency: cart.currency })} away from complimentary shipping`;
    },

    open() { this.refresh(); this.drawer.classList.add('is-open'); this.overlay.classList.add('is-open'); if (lenis) lenis.stop(); },
    close() { this.drawer.classList.remove('is-open'); this.overlay.classList.remove('is-open'); if (lenis) lenis.start(); }
  };

  /* ------------------------------------------------------ Boot */
  function boot() {
    initTextReveals();
    initFades();
    initParallax();
    initDividers();
    initMagnetic();
    initRipple();
    initCursor();
    initCardTilt();
    initGridStagger();
    initPageTransitions();
    Cart.init();
    window.ElysiumCart = Cart;
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', boot)
    : boot();
})();
